import { useCallback, useEffect, useRef, useState } from "react";

import Field from "../../../shared/ui/Field/Field";
import InlineError from "../../../shared/ui/Field/InlineError";
import TextInput from "../../../shared/ui/Field/TextInput";
import Select from "../../../shared/ui/Field/Select";
import Textarea from "../../../shared/ui/Field/Textarea";
import Spinner from "../../../shared/ui/Loading/Spinner";
import CompanyUkrainianDateField from "./CompanyUkrainianDateField";
import { isoFromLocalDateTimeString } from "../lib/ukrainianDateEditor";
import { messageFromError, parseDrfFieldErrors } from "../../../shared/api/drfErrors";
import { fetchCompanyClients } from "../../companyClients/api/companyClients";
import { clientDisplayName } from "../../companyClients/lib/clientLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
  sameMembershipGeneration,
} from "../../companyWorkspace/lib/scopeSession";
import { fetchMembers } from "../../companyWorkspace/api/companyWorkspace";
import {
  isActiveMembership,
  isAdminMembership,
} from "../../companyWorkspace/lib/companyLabels";
import ScopeAwarePropertyPicker, {
  PROPERTY_SCOPE_COMPANY,
} from "../../companyProperties/widgets/ScopeAwarePropertyPicker";
import {
  createCompanyTask,
  createCompanyViewing,
  preflightCompanyTask,
  preflightCompanyViewing,
} from "../api/companyWork";
import {
  isCorporateWorkWritesAvailable,
  memberDisplayName,
  membershipGenerationOf,
  messageFromWorkError,
  TASK_PRIORITY_LABELS,
} from "../lib/workLabels";

function unwrapResults(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function fromLocalInputValue(local) {
  if (!local) return null;
  return isoFromLocalDateTimeString(local);
}

/**
 * Feature-local create modal for corporate Task / Viewing.
 * Lifetime uses scopeSession; each async channel keeps independent local freshness.
 */
function CompanyWorkCreateModal({
  open,
  kind = "task",
  companyId,
  company,
  membership,
  actorId,
  initialClientId = null,
  initialPropertyId = null,
  onClose,
  onCreated,
  writesSuspended = false,
  onBusyChange,
}) {
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);
  const clientsChannelRef = useRef(0);
  const membersChannelRef = useRef(0);
  const preflightChannelRef = useRef(0);
  const generation = membershipGenerationOf(membership);
  const isOa = isAdminMembership(company, membership);
  const writesAvailable = isCorporateWorkWritesAvailable(company);
  const membershipActive = isActiveMembership(company, membership);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [dueLocal, setDueLocal] = useState("");
  const [scheduledLocal, setScheduledLocal] = useState("");
  const [notes, setNotes] = useState("");
  const [clientId, setClientId] = useState(initialClientId);
  const [propertyId, setPropertyId] = useState(initialPropertyId);
  const [clientQuery, setClientQuery] = useState("");
  const [clientOptions, setClientOptions] = useState([]);
  const [clientLoadError, setClientLoadError] = useState(null);
  const [members, setMembers] = useState([]);
  const [responsibleUserId, setResponsibleUserId] = useState(
    actorId != null ? String(actorId) : "",
  );
  const [preflightWarning, setPreflightWarning] = useState(null);
  const [contactWarning, setContactWarning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const clientGroupRef = useRef(null);
  const propertyGroupRef = useRef(null);
  const dueFieldRef = useRef(null);
  const scheduledFieldRef = useRef(null);
  const openRef = useRef(open);
  const focusTimerRef = useRef(null);
  const focusGenRef = useRef(0);
  openRef.current = open;

  const clearProtected = useCallback(() => {
    setClientOptions([]);
    setMembers([]);
    setPreflightWarning(null);
    setContactWarning(false);
    setError(null);
    setFieldErrors({});
  }, []);

  const invalidateWarnings = useCallback(() => {
    preflightChannelRef.current += 1;
    setPreflightWarning(null);
    setContactWarning(false);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    mountedRef.current = true;
    // Lifetime identity only — no resourceId bind so clients/members/preflight
    // channels do not reject each other. Local channel refs own read freshness.
    scopeGate.bindIdentity({
      companyId,
      actorId,
      membershipGeneration: generation,
      bindMembershipGeneration: true,
    });
    clearProtected();
    setTitle("");
    setDescription("");
    setPriority("medium");
    setDueLocal("");
    setScheduledLocal("");
    setNotes("");
    setClientId(initialClientId);
    setPropertyId(initialPropertyId);
    setClientQuery("");
    setResponsibleUserId(actorId != null ? String(actorId) : "");
    clientsChannelRef.current += 1;
    membersChannelRef.current += 1;
    preflightChannelRef.current += 1;
    return () => {
      mountedRef.current = false;
      focusGenRef.current += 1;
      if (focusTimerRef.current != null) {
        window.clearTimeout(focusTimerRef.current);
        focusTimerRef.current = null;
      }
      scopeGate.invalidate();
      busyOpRef.current = null;
      clientsChannelRef.current += 1;
      membersChannelRef.current += 1;
      preflightChannelRef.current += 1;
    };
  }, [
    open,
    companyId,
    actorId,
    generation,
    kind,
    initialClientId,
    initialPropertyId,
    scopeGate,
    clearProtected,
  ]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !busyOpRef.current) {
        if (typeof onClose === "function") onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  const buildPayloadBase = useCallback(
    () => ({
      actor_membership_generation: generation,
      responsible_user_id: responsibleUserId
        ? Number(responsibleUserId)
        : undefined,
    }),
    [generation, responsibleUserId],
  );

  const helperLifetimeStillValid = useCallback(
    (lifetimeSnap) => {
      if (!mountedRef.current) return false;
      if (!scopeGate.lifetimeStillValid({ lifetime: lifetimeSnap }, { mounted: true })) {
        return false;
      }
      const bound = scopeGate.getBoundIdentity();
      if (!sameCompanyId(bound.companyId, companyId)) return false;
      if (!sameActorId(bound.actorId, actorId)) return false;
      if (
        bound.membershipGenerationBound &&
        !sameMembershipGeneration(bound.membershipGeneration, generation)
      ) {
        return false;
      }
      return true;
    },
    [scopeGate, companyId, actorId, generation],
  );

  const loadClients = useCallback(async () => {
    if (!open || !companyId) return;
    // Local channel + lifetime only — do not beginRead (shared readGeneration
    // would cross-invalidate parallel members/preflight helpers).
    const channel = ++clientsChannelRef.current;
    const lifetimeSnap = scopeGate.currentLifetime();
    setClientLoadError(null);
    try {
      const data = await fetchCompanyClients(companyId, {
        search: clientQuery.trim() || undefined,
        page_size: 20,
      });
      if (
        channel !== clientsChannelRef.current ||
        !helperLifetimeStillValid(lifetimeSnap)
      ) {
        return;
      }
      setClientOptions(unwrapResults(data));
    } catch (err) {
      if (
        channel !== clientsChannelRef.current ||
        !helperLifetimeStillValid(lifetimeSnap)
      ) {
        return;
      }
      setClientOptions([]);
      setClientLoadError(messageFromError(err));
    }
  }, [open, companyId, clientQuery, scopeGate, helperLifetimeStillValid]);

  const loadMembers = useCallback(async () => {
    if (!open || !companyId || !isOa) return;
    const channel = ++membersChannelRef.current;
    const lifetimeSnap = scopeGate.currentLifetime();
    try {
      const data = await fetchMembers(companyId, "active");
      if (
        channel !== membersChannelRef.current ||
        !helperLifetimeStillValid(lifetimeSnap)
      ) {
        return;
      }
      setMembers(unwrapResults(data));
    } catch {
      if (
        channel !== membersChannelRef.current ||
        !helperLifetimeStillValid(lifetimeSnap)
      ) {
        return;
      }
      setMembers([]);
    }
  }, [open, companyId, isOa, scopeGate, helperLifetimeStillValid]);

  useEffect(() => {
    if (!open) return;
    loadClients();
  }, [open, loadClients]);

  useEffect(() => {
    if (!open) return;
    loadMembers();
  }, [open, loadMembers]);

  const runPreflight = useCallback(async () => {
    if (!open || !companyId || generation == null) return null;
    const channel = ++preflightChannelRef.current;
    const lifetimeSnap = scopeGate.currentLifetime();
    try {
      let data;
      const base = buildPayloadBase();
      if (kind === "viewing") {
        if (!clientId || !propertyId || !scheduledLocal) return null;
        data = await preflightCompanyViewing(companyId, {
          ...base,
          client_id: Number(clientId),
          property_id: Number(propertyId),
          scheduled_at: fromLocalInputValue(scheduledLocal),
          notes,
        });
      } else {
        if (!title.trim()) return null;
        data = await preflightCompanyTask(companyId, {
          ...base,
          title: title.trim(),
          description,
          priority,
          due_date: fromLocalInputValue(dueLocal),
          client_id: clientId ? Number(clientId) : null,
          property_id: propertyId ? Number(propertyId) : null,
        });
      }
      if (
        channel !== preflightChannelRef.current ||
        !helperLifetimeStillValid(lifetimeSnap)
      ) {
        return null;
      }
      setContactWarning(Boolean(data?.executor_lacks_client_contact_access));
      if (data?.warning) setPreflightWarning(String(data.warning));
      else setPreflightWarning(null);
      return { ok: true, data };
    } catch (err) {
      if (
        channel !== preflightChannelRef.current ||
        !helperLifetimeStillValid(lifetimeSnap)
      ) {
        return null;
      }
      const message = messageFromWorkError(err);
      setPreflightWarning(message);
      setContactWarning(false);
      return { ok: false, error: message, err };
    }
  }, [
    open,
    companyId,
    generation,
    kind,
    buildPayloadBase,
    clientId,
    propertyId,
    scheduledLocal,
    notes,
    title,
    description,
    priority,
    dueLocal,
    scopeGate,
    helperLifetimeStillValid,
  ]);

  useEffect(() => {
    if (!open) return undefined;
    invalidateWarnings();
    const handle = setTimeout(() => {
      runPreflight();
    }, 250);
    return () => clearTimeout(handle);
  }, [
    open,
    kind,
    responsibleUserId,
    clientId,
    propertyId,
    scheduledLocal,
    notes,
    title,
    description,
    priority,
    dueLocal,
    invalidateWarnings,
    runPreflight,
  ]);

  const clearFieldError = useCallback((key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const scrollFieldIntoView = useCallback((node) => {
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ block: "center", inline: "nearest" });
    }
  }, []);

  const focusCreateField = useCallback((key) => {
    if (key === "title") {
      const input = document.getElementById("company-work-create-title");
      const group = input?.closest(".ui-field") || input;
      scrollFieldIntoView(group);
      input?.focus();
      return;
    }
    if (key === "scheduled" || key === "due") {
      const input = document.getElementById(
        key === "due"
          ? "company-work-create-due"
          : "company-work-create-scheduled",
      );
      const group = input?.closest(".ui-field") || input;
      scrollFieldIntoView(group);
      input?.focus();
      return;
    }
    if (key === "client") {
      scrollFieldIntoView(clientGroupRef.current);
      clientGroupRef.current
        ?.querySelector("input, button, select, textarea")
        ?.focus();
      return;
    }
    if (key === "property") {
      scrollFieldIntoView(propertyGroupRef.current);
      propertyGroupRef.current
        ?.querySelector("input, button, select, textarea")
        ?.focus();
    }
  }, [scrollFieldIntoView]);

  const scheduleFocus = useCallback(
    (key) => {
      focusGenRef.current += 1;
      const gen = focusGenRef.current;
      if (focusTimerRef.current != null) {
        window.clearTimeout(focusTimerRef.current);
      }
      focusTimerRef.current = window.setTimeout(() => {
        focusTimerRef.current = null;
        if (!mountedRef.current || !openRef.current) return;
        if (gen !== focusGenRef.current) return;
        focusCreateField(key);
      }, 0);
    },
    [focusCreateField],
  );

  const submit = async (event) => {
    event.preventDefault();
    if (!companyId || generation == null || busyOpRef.current || writesSuspended) return;
    if (!membershipActive || !writesAvailable) {
      setError("Запис недоступний для поточного членства.");
      return;
    }
    const nextErrors = {};
    let dueValue = dueLocal;
    let scheduledValue = scheduledLocal;
    if (kind === "viewing") {
      const scheduledCommit = scheduledFieldRef.current?.commit?.();
      if (!scheduledCommit?.ok || !scheduledCommit.value) {
        nextErrors.scheduled =
          scheduledCommit?.error || "Вкажіть дату та час перегляду.";
      } else {
        scheduledValue = scheduledCommit.value;
      }
      if (!clientId) {
        nextErrors.client = "Оберіть клієнта компанії.";
      }
      if (!propertyId) {
        nextErrors.property = "Оберіть обʼєкт компанії.";
      }
    } else {
      if (!title.trim()) {
        nextErrors.title = "Вкажіть назву задачі.";
      }
      const dueCommit = dueFieldRef.current?.commit?.();
      if (dueCommit && dueCommit.ok === false) {
        nextErrors.due = dueCommit.error;
      } else if (dueCommit) {
        dueValue = dueCommit.value;
      }
    }
    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      const firstKey = ["title", "due", "scheduled", "client", "property"].find(
        (key) => nextErrors[key],
      );
      if (firstKey) scheduleFocus(firstKey);
      return;
    }

    const op = scopeGate.beginMutation({
      companyId,
      actorId,
      membershipGeneration: generation,
    });
    busyOpRef.current = op;
    setBusy(true);
    if (typeof onBusyChange === "function") onBusyChange(true);
    setError(null);
    setFieldErrors({});
    try {
      const preflight = await runPreflight();
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      if (!preflight || preflight.ok !== true) {
        setError(
          preflight?.error ||
            preflightWarning ||
            "Перевірка перед створенням не пройшла.",
        );
        return;
      }
      if (preflight.data?.executor_lacks_client_contact_access) {
        setContactWarning(true);
      }

      let created;
      const base = buildPayloadBase();
      if (kind === "viewing") {
        const scheduled = fromLocalInputValue(scheduledValue);
        if (!scheduled) {
          throw new Error("Вкажіть дату та час перегляду.");
        }
        created = await createCompanyViewing(companyId, {
          ...base,
          client_id: Number(clientId),
          property_id: Number(propertyId),
          scheduled_at: scheduled,
          notes,
        });
      } else {
        created = await createCompanyTask(companyId, {
          ...base,
          title: title.trim(),
          description,
          priority,
          due_date: fromLocalInputValue(dueValue),
          client_id: clientId ? Number(clientId) : null,
          property_id: propertyId ? Number(propertyId) : null,
        });
      }
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      if (typeof onCreated === "function") {
        onCreated(created, kind);
        // Parent owns dismiss/navigation after successful create. Calling
        // onClose here raced list replaceFilters against detail navigate.
        return;
      }
      if (typeof onClose === "function") onClose();
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      // Denied writes are not auto-retried.
      const status = err?.response?.status;
      if (status === 400) {
        const moderatedKeys =
          kind === "viewing"
            ? ["notes", "scheduled_at", "client_id", "property_id"]
            : ["title", "description", "due_date", "priority", "client_id", "property_id"];
        const parsed = parseDrfFieldErrors(err, moderatedKeys);
        const { _form, ...fields } = parsed;
        if (fields.due_date && !fields.due) {
          fields.due = fields.due_date;
          delete fields.due_date;
        }
        if (fields.scheduled_at && !fields.scheduled) {
          fields.scheduled = fields.scheduled_at;
          delete fields.scheduled_at;
        }
        if (fields.client_id && !fields.client) {
          fields.client = fields.client_id;
          delete fields.client_id;
        }
        if (fields.property_id && !fields.property) {
          fields.property = fields.property_id;
          delete fields.property_id;
        }
        setFieldErrors((prev) => ({ ...prev, ...fields }));
        setError(_form || null);
        return;
      }
      setError(messageFromWorkError(err));
    } finally {
      if (busyOpRef.current === op) {
        busyOpRef.current = null;
        setBusy(false);
        if (typeof onBusyChange === "function") onBusyChange(false);
      }
    }
  };

  if (!open) return null;

  // Parent must pass live company/membership; require write gate + active
  // membership (not tautological actor/generation self-compares).
  const identityAlive =
    companyId != null &&
    sameCompanyId(companyId, company?.id) &&
    actorId != null &&
    membership != null &&
    generation != null &&
    membershipActive &&
    writesAvailable;

  if (!identityAlive) return null;

  const dialogTitle =
    kind === "viewing" ? "Новий перегляд" : "Нова задача";
  const dialogLabel =
    kind === "viewing" ? "Створити перегляд" : "Створити задачу";

  return (
    <div
      className="feature-clients-list__modal feature-company-workspace__modal"
      role="dialog"
      aria-modal="true"
      aria-label={dialogLabel}
    >
      <div
        className="feature-clients-list__modal-backdrop"
        aria-hidden="true"
        onClick={busy ? undefined : onClose}
      />
      <div className="feature-clients-list__modal-dialog">
        <header className="feature-clients-list__modal-head">
          <h2 className="feature-clients-list__modal-title">{dialogTitle}</h2>
          <button
            type="button"
            className="feature-clients-list__modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Закрити"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <form className="ui-form feature-company-work__modal-form" onSubmit={submit}>
        <div className="feature-clients-list__modal-body">
          {kind === "task" ? (
            <fieldset className="feature-company-work__modal-group">
              <legend>Деталі роботи</legend>
              <Field
                id="company-work-create-title"
                label="Назва"
                required
                error={fieldErrors.title}
                className="ui-form__field"
              >
                <TextInput
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    clearFieldError("title");
                  }}
                  onInvalid={(e) => {
                    e.preventDefault();
                    setFieldErrors((prev) => ({
                      ...prev,
                      title: "Вкажіть назву задачі.",
                    }));
                    scheduleFocus("title");
                  }}
                  required
                  disabled={busy || writesSuspended}
                />
              </Field>
              <Field
                id="company-work-create-description"
                label="Опис"
                error={fieldErrors.description}
                className="ui-form__field"
              >
                <Textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    clearFieldError("description");
                  }}
                  disabled={busy || writesSuspended}
                  rows={3}
                />
              </Field>
              <label className="ui-form__field">
                <span>Пріоритет</span>
                <Select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  disabled={busy || writesSuspended}
                  options={Object.entries(TASK_PRIORITY_LABELS).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                />
              </label>
              <CompanyUkrainianDateField
                ref={dueFieldRef}
                id="company-work-create-due"
                label="Дедлайн (необовʼязково)"
                mode="datetime"
                canonicalValue={dueLocal}
                onCanonicalChange={(value) => {
                  setDueLocal(value);
                  clearFieldError("due");
                }}
                error={fieldErrors.due}
                disabled={busy || writesSuspended}
              />
            </fieldset>
          ) : (
            <fieldset className="feature-company-work__modal-group">
              <legend>Розклад і виконавець</legend>
              <CompanyUkrainianDateField
                ref={scheduledFieldRef}
                id="company-work-create-scheduled"
                label="Дата і час"
                required
                mode="datetime"
                canonicalValue={scheduledLocal}
                onCanonicalChange={(value) => {
                  setScheduledLocal(value);
                  clearFieldError("scheduled");
                }}
                error={fieldErrors.scheduled}
                disabled={busy || writesSuspended}
                onInvalid={(e) => {
                  e.preventDefault();
                  setFieldErrors((prev) => ({
                    ...prev,
                    scheduled: "Вкажіть дату та час перегляду.",
                  }));
                  scheduleFocus("scheduled");
                }}
              />
              {isOa ? (
                <label className="ui-form__field">
                  <span>Виконавець</span>
                  <Select
                    value={responsibleUserId}
                    onChange={(e) => setResponsibleUserId(e.target.value)}
                    disabled={busy || writesSuspended}
                    options={members
                      .map((m) => {
                        const userId = m.user?.id ?? m.user_id;
                        if (userId == null) return null;
                        return {
                          value: String(userId),
                          label: memberDisplayName(m) || `Учасник #${userId}`,
                        };
                      })
                      .filter(Boolean)}
                  />
                </label>
              ) : (
                <p className="feature-company-workspace__meta">
                  Виконавець: ви
                </p>
              )}
              <Field
                id="company-work-create-notes"
                label="Нотатки"
                error={fieldErrors.notes}
                className="ui-form__field"
              >
                <Textarea
                  value={notes}
                  onChange={(e) => {
                    setNotes(e.target.value);
                    clearFieldError("notes");
                  }}
                  disabled={busy || writesSuspended}
                  rows={3}
                />
              </Field>
            </fieldset>
          )}

          {kind === "task" ? (
            <fieldset className="feature-company-work__modal-group">
              <legend>Виконавець</legend>
              {isOa ? (
                <Select
                  aria-label="Виконавець"
                  value={responsibleUserId}
                  onChange={(e) => setResponsibleUserId(e.target.value)}
                  disabled={busy || writesSuspended}
                  options={members
                    .map((m) => {
                      const userId = m.user?.id ?? m.user_id;
                      if (userId == null) return null;
                      return {
                        value: String(userId),
                        label: memberDisplayName(m) || `Учасник #${userId}`,
                      };
                    })
                    .filter(Boolean)}
                />
              ) : (
                <p className="feature-company-workspace__meta">
                  Виконавець: ви
                </p>
              )}
            </fieldset>
          ) : null}

          <fieldset
            ref={clientGroupRef}
            className="feature-company-work__modal-group"
            aria-invalid={fieldErrors.client ? "true" : undefined}
            aria-describedby={
              fieldErrors.client ? "company-work-create-client-error" : undefined
            }
          >
            <legend>
              Клієнт
              {kind === "viewing" ? (
                <span className="ui-field__required" aria-hidden="true">
                  *
                </span>
              ) : null}
            </legend>
          <div className="ui-form__field">
            <div className="feature-client-detail__property-search">
              <TextInput
                aria-label="Пошук клієнта"
                value={clientQuery}
                onChange={(e) => setClientQuery(e.target.value)}
                placeholder="Пошук…"
                disabled={busy || writesSuspended}
                aria-invalid={fieldErrors.client ? "true" : undefined}
                aria-describedby={
                  fieldErrors.client
                    ? "company-work-create-client-error"
                    : undefined
                }
              />
              <button
                type="button"
                className="ui-form__button ui-form__button--ghost"
                onClick={loadClients}
                disabled={busy || writesSuspended}
              >
                Шукати
              </button>
            </div>
            {clientLoadError ? (
              <p className="feature-company-workspace__error" role="alert">
                {clientLoadError}
              </p>
            ) : null}
            <ul className="feature-client-detail__property-options" role="listbox">
              {kind === "task" ? (
                <li>
                  <button
                    type="button"
                    role="option"
                    aria-selected={clientId == null}
                    className={[
                      "feature-client-detail__property-option",
                      clientId == null
                        ? "feature-client-detail__property-option--selected"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    disabled={busy || writesSuspended}
                    onClick={() => {
                      setClientId(null);
                      clearFieldError("client");
                    }}
                  >
                    Без клієнта
                  </button>
                </li>
              ) : null}
              {clientOptions.map((row) => {
                const selected = clientId != null && String(clientId) === String(row.id);
                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={[
                        "feature-client-detail__property-option",
                        selected
                          ? "feature-client-detail__property-option--selected"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      disabled={busy || writesSuspended}
                      onClick={() => {
                        setClientId(row.id);
                        clearFieldError("client");
                      }}
                    >
                      {clientDisplayName(row)}
                    </button>
                  </li>
                );
              })}
            </ul>
            {fieldErrors.client ? (
              <InlineError id="company-work-create-client-error">
                {fieldErrors.client}
              </InlineError>
            ) : null}
          </div>
          </fieldset>

          <fieldset
            ref={propertyGroupRef}
            className="feature-company-work__modal-group"
            aria-invalid={fieldErrors.property ? "true" : undefined}
            aria-describedby={
              fieldErrors.property
                ? "company-work-create-property-error"
                : undefined
            }
          >
            <legend>
              Обʼєкт
              {kind === "viewing" ? (
                <span className="ui-field__required" aria-hidden="true">
                  *
                </span>
              ) : null}
            </legend>
          <div className="ui-form__field">
            <ScopeAwarePropertyPicker
              scope={PROPERTY_SCOPE_COMPANY}
              companyId={companyId}
              actorId={actorId}
              membershipGeneration={generation}
              value={propertyId}
              onChange={(id) => {
                setPropertyId(id);
                clearFieldError("property");
              }}
              disabled={busy || writesSuspended}
              label="Об'єкт нерухомості компанії"
              searchInvalid={Boolean(fieldErrors.property)}
              searchDescribedBy={
                fieldErrors.property
                  ? "company-work-create-property-error"
                  : undefined
              }
            />
            {kind === "task" ? (
              <button
                type="button"
                className="ui-form__button ui-form__button--ghost"
                disabled={busy || propertyId == null}
                onClick={() => {
                  setPropertyId(null);
                  clearFieldError("property");
                }}
              >
                Без обʼєкта
              </button>
            ) : null}
            {fieldErrors.property ? (
              <InlineError id="company-work-create-property-error">
                {fieldErrors.property}
              </InlineError>
            ) : null}
          </div>
          </fieldset>

          {contactWarning ? (
            <p className="feature-company-workspace__meta" role="status">
              Увага: виконавець не має доступу до контактів клієнта. Це не блокує
              створення перегляду.
            </p>
          ) : null}
          {preflightWarning ? (
            <p className="feature-company-workspace__error" role="alert">
              {preflightWarning}
            </p>
          ) : null}
          {error ? (
            <p className="feature-company-workspace__error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
          <div className="ui-form__actions feature-company-work__modal-actions">
            <button
              type="submit"
              className="ui-form__button ui-form__button--primary"
              disabled={busy || writesSuspended}
            >
              {busy ? <Spinner size="sm" label="Збереження" /> : "Створити"}
            </button>
            <button
              type="button"
              className="ui-form__button ui-form__button--ghost"
              onClick={onClose}
              disabled={busy || writesSuspended}
            >
              Скасувати
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CompanyWorkCreateModal;
export { fromLocalInputValue };
